import { defineStore } from 'pinia';
import type {
  ModalState,
  ModalProps,
  SuccessModalProps,
} from '@/shared/ui/AppModal/model/types/appModal';

export const useModalStore = defineStore('modal', () => {
  const modalProps: Ref<ModalProps | null> = ref(null);
  const successProps: Ref<SuccessModalProps | null> = ref(null);

  const modalState = ref<ModalState>({
    auth: false,
    callback: false,
    complaint: false,
    feedback: false,
    contacts: false,
    image: false,
    dateTime: false,
    consultation: false,
    confirmation: false,
    success: false,
  });

  const scrollLockOwners: Ref<Set<string>> = ref(new Set());
  const isScrollLocked: ComputedRef<boolean> = computed(
    () => scrollLockOwners.value.size > 0,
  );

  const isAuthModalOpen = computed(() => modalState.value.auth);
  const isImageModalOpen = computed(() => modalState.value.image);
  const isProfileModalOpen = computed(
    () =>
      modalState.value.callback ||
      modalState.value.complaint ||
      modalState.value.feedback ||
      modalState.value.contacts,
  );

  const setModalState = (
    modalType: keyof ModalState,
    value: boolean = false,
    props?: ModalProps,
  ) => {
    modalState.value[modalType] = value;
    modalProps.value = props || null;
  };

  const setScrollLockOwner = (owner: string, isLocked: boolean): void => {
    if (isLocked) {
      scrollLockOwners.value.add(owner);
    } else {
      scrollLockOwners.value.delete(owner);
    }
  };

  const openSuccess = (props: SuccessModalProps) => {
    successProps.value = props;
    modalState.value.success = true;
  };

  const closeAllModals = () => {
    modalState.value.auth = false;
    modalState.value.callback = false;
    modalState.value.complaint = false;
    modalState.value.feedback = false;
    modalState.value.contacts = false;
    modalState.value.image = false;
    modalState.value.dateTime = false;
    modalState.value.consultation = false;
    modalState.value.confirmation = false;
    modalState.value.success = false;
    successProps.value = null;
  };

  return {
    modalState,
    modalProps,
    successProps,
    scrollLockOwners,
    isScrollLocked,
    isAuthModalOpen,
    isImageModalOpen,
    isProfileModalOpen,
    setModalState,
    setScrollLockOwner,
    openSuccess,
    closeAllModals,
  };
});
